// Value editors shared by the typed widgets (spec/dashboard.md sections 2.2 and 6.4): duration (with presets and a text
// parse), date-time in the display zone (with the calendar picker), colour, tags, and the time-zone picker.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from './Button';
import { Chip } from './Chip';
import { CalendarDays, ChevronLeft, ChevronRight, Clock } from './icons';
import { Select, TextInput } from './inputs';
import { Popover, type PopoverCloseReason } from './Popover';
import { formatDuration, formatInstant, parseDuration, parseLocalInput, useTimeZone, zoneLabel, type DisplayZone, type ZoneContext } from './time';

export function TimeZonePicker({ ctx = {}, id }: { ctx?: ZoneContext; id?: string }) {
  const [zone, setZone] = useTimeZone();
  const zones: DisplayZone[] = ['utc', 'env:dev', 'env:stage', 'local'];
  return (
    <Select id={id} size="s" aria-label="Display time zone" value={zone} onChange={(e) => setZone(e.target.value as DisplayZone)}>
      {zones.map((z) => (
        <option key={z} value={z}>
          {zoneLabel(z, ctx)}
        </option>
      ))}
    </Select>
  );
}

export interface DurationPreset {
  label: string;
  seconds: number;
}

/** The presets of a LiveOps duration (an offer's duration or cooldown, a repeat): the usual windows at a click. */
export const COMMON_DURATION_PRESETS: DurationPreset[] = [
  { label: '1 h', seconds: 3600 },
  { label: '3 h', seconds: 3 * 3600 },
  { label: '1 d', seconds: 86400 },
  { label: '3 d', seconds: 3 * 86400 },
  { label: '7 d', seconds: 7 * 86400 },
];

export interface DurationInputProps {
  /** seconds */
  value: number | null | undefined;
  onCommit: (seconds: number) => void;
  /** the word 0 reads as ("no cooldown", "none"); the fields are empty then */
  zeroMeans?: string;
  /** toggle chips above the boxes (spec section 6.4); a `zeroMeans` chip is prepended when no preset is 0 */
  presets?: DurationPreset[];
  disabled?: boolean;
  id?: string;
  'aria-label'?: string;
}

type DurationParts = { d: number; h: number; m: number; s: number };

function splitDuration(s: number | null | undefined): DurationParts {
  const total = Math.max(0, Math.floor(s ?? 0));
  return { d: Math.floor(total / 86400), h: Math.floor((total % 86400) / 3600), m: Math.floor((total % 3600) / 60), s: total % 60 };
}

/**
 * D H M S fields that commit as one change; 0 renders as the owner's word (spec section 2.2, Q-E-3). Optional preset
 * chips, and any box takes a typed or pasted `1d 12h` / `90m` / `2:30:00` (parsed by `parseDuration`, section 6.4).
 */
export function DurationInput({ value, onCommit, zeroMeans, presets, disabled, id, ...rest }: DurationInputProps) {
  const label = rest['aria-label'] ?? 'Duration';
  const [parts, setParts] = useState(splitDuration(value));
  /** a box being typed as text (a letter, a colon or a space was typed into it) */
  const [textBox, setTextBox] = useState<{ key: keyof DurationParts; text: string } | null>(null);
  useEffect(() => {
    setParts(splitDuration(value));
    setTextBox(null);
  }, [value]);
  const commitTotal = (seconds: number) => {
    setParts(splitDuration(seconds));
    setTextBox(null);
    if (seconds !== (value ?? 0)) onCommit(seconds);
  };
  const commit = (next: DurationParts) => {
    setParts(next);
    commitTotal(next.d * 86400 + next.h * 3600 + next.m * 60 + next.s);
  };
  const commitBox = (key: keyof DurationParts) => {
    if (textBox && textBox.key === key) {
      const t = textBox.text.trim();
      if (/^\d+$/.test(t)) commit({ ...parts, [key]: Number(t) });
      else {
        const parsed = t === '' ? null : parseDuration(t);
        if (parsed === null) {
          setTextBox(null);
          setParts(splitDuration(value));
        } else commitTotal(parsed);
      }
      return;
    }
    commit(parts);
  };
  const isZero = (value ?? 0) === 0;
  const unit = (key: keyof DurationParts, unitLabel: string, max?: number) => {
    const asText = textBox?.key === key;
    return (
      <span className="unit">
        <input
          className="input input-s"
          type={asText ? 'text' : 'number'}
          min={asText ? undefined : 0}
          max={asText ? undefined : max}
          aria-label={`${label} ${unitLabel}`}
          disabled={disabled}
          value={asText ? textBox.text : isZero && zeroMeans && parts[key] === 0 ? '' : parts[key]}
          placeholder={isZero && zeroMeans ? '0' : undefined}
          onChange={(e) => {
            if (asText) setTextBox({ key, text: e.target.value });
            else setParts({ ...parts, [key]: Math.max(0, Number(e.target.value) || 0) });
          }}
          onPaste={(e) => {
            const pasted = e.clipboardData.getData('text');
            if (/^\s*\d+\s*$/.test(pasted)) return;
            const parsed = parseDuration(pasted);
            e.preventDefault();
            if (parsed !== null) commitTotal(parsed);
          }}
          onBlur={() => commitBox(key)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitBox(key);
            } else if (e.key === 'Escape') {
              setTextBox(null);
              setParts(splitDuration(value));
            } else if (!asText && e.key.length === 1 && /[a-z:\s]/i.test(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey) {
              // a unit letter, a colon or a space turns the box into a text field for this edit: `1d 12h` lands as one value
              e.preventDefault();
              const current = (e.target as HTMLInputElement).value;
              setTextBox({ key, text: `${current}${e.key}` });
            }
          }}
        />
        <span>{unitLabel}</span>
      </span>
    );
  };
  const chips: DurationPreset[] = presets ? [...(zeroMeans && !presets.some((p) => p.seconds === 0) ? [{ label: zeroMeans, seconds: 0 }] : []), ...presets] : [];
  const boxes = (
    <span className="duration" id={id} role="group" aria-label={label}>
      {unit('d', 'd')}
      {unit('h', 'h', 23)}
      {unit('m', 'm', 59)}
      {unit('s', 's', 59)}
      <span className="muted small" aria-live="polite">
        {formatDuration(value ?? 0, zeroMeans)}
      </span>
    </span>
  );
  if (chips.length === 0) return boxes;
  return (
    <span className="duration-wrap">
      <span className="duration-presets" role="group" aria-label={`${label} presets`}>
        {chips.map((p) => (
          <button
            key={`${p.seconds}:${p.label}`}
            type="button"
            className="value-chip"
            aria-pressed={(value ?? 0) === p.seconds}
            disabled={disabled}
            onClick={() => commitTotal(p.seconds)}
          >
            {p.label}
          </button>
        ))}
      </span>
      {boxes}
    </span>
  );
}

export interface DateTimeInputProps {
  /** unix seconds (UTC) */
  value: number | null | undefined;
  onCommit: (seconds: number | null) => void;
  ctx?: ZoneContext;
  allowEmpty?: boolean;
  disabled?: boolean;
  id?: string;
  'aria-label'?: string;
}

/**
 * An instant typed in the display zone (`YYYY-MM-DD HH:mm`), stored as unix seconds, always with the zone label; the
 * calendar button opens a month view, a time row and quick chips (spec section 6.4). The picker builds the same
 * `YYYY-MM-DD HH:mm:ss` text the field takes and runs it through `parseLocalInput`, so both read one zone.
 */
export function DateTimeInput({ value, onCommit, ctx = {}, allowEmpty, disabled, id, ...rest }: DateTimeInputProps) {
  const [zone] = useTimeZone();
  const wrapRef = useRef<HTMLSpanElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const label = rest['aria-label'] ?? 'Date and time';
  const closePicker = useCallback((reason: PopoverCloseReason) => {
    setOpen(false);
    if (reason !== 'outside') inputRef.current?.focus();
  }, []);
  const text = useMemo(() => (value === null || value === undefined ? '' : formatInstant(value, zone, ctx)), [value, zone, ctx]);
  const [draft, setDraft] = useState(text);
  const [invalid, setInvalid] = useState(false);
  useEffect(() => {
    setDraft(text);
    setInvalid(false);
  }, [text]);
  const commit = () => {
    if (draft.trim() === '') {
      if (allowEmpty) {
        if (value !== null && value !== undefined) onCommit(null);
        setInvalid(false);
      } else {
        setDraft(text);
      }
      return;
    }
    const parsed = parseLocalInput(draft, zone, ctx);
    if (parsed === null) {
      setInvalid(true);
      setDraft(text);
      return;
    }
    setInvalid(false);
    if (parsed !== value) onCommit(parsed);
  };
  const pick = (seconds: number | null) => {
    setInvalid(false);
    if (seconds !== value && !(seconds === null && (value === null || value === undefined))) onCommit(seconds);
  };
  return (
    <span className="datetime">
      <span className="datetime-field" ref={wrapRef}>
        <TextInput
          ref={inputRef}
          id={id}
          size="s"
          aria-label={label}
          placeholder="YYYY-MM-DD HH:mm"
          value={draft}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            } else if (e.key === 'Escape') setDraft(text);
          }}
        />
        <button
          type="button"
          className="icon-button datetime-pick"
          aria-label={`Pick a date (${label})`}
          title="Pick a date"
          aria-haspopup="dialog"
          aria-expanded={open}
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
        >
          <CalendarDays aria-hidden="true" />
        </button>
      </span>
      <span className="zone-label">{zoneLabel(zone, ctx)}</span>
      <Popover anchor={wrapRef.current} open={open} onClose={closePicker} label={`Pick a date (${label})`} className="dt-picker">
        <DatePickerBody value={value} zone={zone} ctx={ctx} allowEmpty={allowEmpty} onPick={pick} onDone={() => closePicker('done')} />
      </Popover>
    </span>
  );
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

const pad2 = (n: number) => (n < 10 ? `0${n}` : String(n));
/** a calendar date as `YYYY-MM-DD`; the arithmetic is on the proleptic calendar (UTC), never on the browser's zone */
function ymd(y: number, m: number, d: number): string {
  const t = new Date(Date.UTC(y, m, d));
  return `${t.getUTCFullYear()}-${pad2(t.getUTCMonth() + 1)}-${pad2(t.getUTCDate())}`;
}
function splitYmd(text: string): { y: number; m: number; d: number } {
  return { y: Number(text.slice(0, 4)), m: Number(text.slice(5, 7)) - 1, d: Number(text.slice(8, 10)) };
}

function DatePickerBody({ value, zone, ctx, allowEmpty, onPick, onDone }: { value: number | null | undefined; zone: DisplayZone; ctx: ZoneContext; allowEmpty?: boolean; onPick: (seconds: number | null) => void; onDone: () => void }) {
  const has = value !== null && value !== undefined;
  // the value and today in the display zone, `YYYY-MM-DD HH:mm:ss`
  const current = has ? formatInstant(value, zone, ctx, { seconds: true }) : null;
  const today = formatInstant(Math.floor(Date.now() / 1000), zone, ctx).slice(0, 10);
  const selectedDay = current?.slice(0, 10) ?? null;
  const timeOfDay = current?.slice(11) ?? '00:00:00';
  const [view, setView] = useState(() => {
    const { y, m } = splitYmd(selectedDay ?? today);
    return { y, m };
  });
  useEffect(() => {
    if (selectedDay) {
      const { y, m } = splitYmd(selectedDay);
      setView((v) => (v.y === y && v.m === m ? v : { y, m }));
    }
  }, [selectedDay]);
  const [timeDraft, setTimeDraft] = useState(timeOfDay.slice(0, 5));
  const [timeInvalid, setTimeInvalid] = useState(false);
  useEffect(() => {
    setTimeDraft(timeOfDay.slice(0, 5));
    setTimeInvalid(false);
  }, [timeOfDay]);

  /** the one way the picker makes an instant: the field's own text parse in the display zone */
  const at = (day: string, time: string) => parseLocalInput(`${day} ${time}`, zone, ctx);
  const commitAt = (day: string, time: string) => {
    const s = at(day, time);
    if (s !== null) onPick(s);
  };
  const shiftDays = (n: number) => {
    const { y, m, d } = splitYmd(selectedDay ?? today);
    commitAt(ymd(y, m, d + n), has ? timeOfDay : formatInstant(Math.floor(Date.now() / 1000), zone, ctx).slice(11, 16));
  };
  const commitTime = () => {
    const mt = /^\s*(\d{1,2}):?(\d{2})\s*$/.exec(timeDraft);
    if (!mt || Number(mt[1]) > 23 || Number(mt[2]) > 59) {
      setTimeInvalid(true);
      setTimeDraft(timeOfDay.slice(0, 5));
      return;
    }
    setTimeInvalid(false);
    commitAt(selectedDay ?? today, `${pad2(Number(mt[1]))}:${mt[2]}`);
  };

  // six Monday-first weeks around the viewed month
  const lead = (new Date(Date.UTC(view.y, view.m, 1)).getUTCDay() + 6) % 7;
  const cells = Array.from({ length: 42 }, (_, i) => {
    const day = ymd(view.y, view.m, 1 - lead + i);
    return { day, n: Number(day.slice(8)), out: splitYmd(day).m !== ((view.m % 12) + 12) % 12 };
  });
  const move = (delta: number) => setView((v) => {
    const t = new Date(Date.UTC(v.y, v.m + delta, 1));
    return { y: t.getUTCFullYear(), m: t.getUTCMonth() };
  });
  const title = `${MONTHS[view.m]} ${view.y}`;
  return (
    <div className="dt-body">
      <div className="dt-head">
        <button type="button" className="icon-button" aria-label="Previous month" onClick={() => move(-1)}>
          <ChevronLeft aria-hidden="true" />
        </button>
        <span className="dt-title" aria-live="polite">
          {title}
        </span>
        <button type="button" className="icon-button" aria-label="Next month" onClick={() => move(1)}>
          <ChevronRight aria-hidden="true" />
        </button>
      </div>
      <div className="dt-grid" role="group" aria-label={title}>
        {WEEKDAYS.map((w) => (
          <span key={w} className="dt-dow" aria-hidden="true">
            {w}
          </span>
        ))}
        {cells.map((c) => (
          <button
            key={c.day}
            type="button"
            className={`dt-day${c.out ? ' out' : ''}${c.day === today ? ' today' : ''}`}
            aria-label={c.day}
            aria-pressed={c.day === selectedDay}
            aria-current={c.day === today ? 'date' : undefined}
            onClick={() => {
              if (c.out) setView({ y: splitYmd(c.day).y, m: splitYmd(c.day).m });
              commitAt(c.day, timeOfDay);
            }}
          >
            {c.n}
          </button>
        ))}
      </div>
      <div className="dt-time">
        <Clock aria-hidden="true" />
        <TextInput
          size="s"
          className="mono"
          aria-label="Time (HH:MM, 24 h)"
          placeholder="HH:MM"
          value={timeDraft}
          aria-invalid={timeInvalid || undefined}
          onChange={(e) => setTimeDraft(e.target.value)}
          onBlur={commitTime}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitTime();
            }
          }}
        />
      </div>
      <div className="dt-chips" role="group" aria-label="Quick picks">
        <button type="button" className="value-chip" onClick={() => {
          const now = formatInstant(Math.floor(Date.now() / 1000), zone, ctx);
          commitAt(now.slice(0, 10), now.slice(11, 16));
        }}>
          Now
        </button>
        <button type="button" className="value-chip" onClick={() => commitAt(selectedDay ?? today, '00:00')}>
          Start of day
        </button>
        <button type="button" className="value-chip" onClick={() => shiftDays(1)}>
          +1 day
        </button>
        <button type="button" className="value-chip" onClick={() => shiftDays(7)}>
          +7 days
        </button>
      </div>
      <div className="dt-foot">
        <span className="zone-label">{zoneLabel(zone, ctx)}</span>
        <span className="dt-foot-actions">
          {allowEmpty && (
            <Button size="s" variant="ghost" onClick={() => onPick(null)} disabled={!has}>
              Clear
            </Button>
          )}
          <Button size="s" variant="primary" onClick={onDone}>
            Done
          </Button>
        </span>
      </div>
    </div>
  );
}

export function ColorInput({ value, onCommit, disabled, id, ...rest }: { value: string | null | undefined; onCommit: (v: string) => void; disabled?: boolean; id?: string; 'aria-label'?: string }) {
  const normalized = (value ?? '#000000FF').toUpperCase();
  const [text, setText] = useState(normalized);
  useEffect(() => setText(normalized), [normalized]);
  const valid = /^#[0-9A-F]{8}$/.test(text) || /^#[0-9A-F]{6}$/.test(text);
  const commit = () => {
    if (!valid) {
      setText(normalized);
      return;
    }
    const full = text.length === 7 ? `${text}FF` : text;
    if (full !== normalized) onCommit(full);
  };
  return (
    <span className="color-input">
      <input
        type="color"
        aria-label={`${rest['aria-label'] ?? 'Colour'} picker`}
        disabled={disabled}
        value={normalized.slice(0, 7)}
        onChange={(e) => {
          const alpha = normalized.slice(7) || 'FF';
          onCommit(`${e.target.value.toUpperCase()}${alpha}`);
        }}
      />
      <TextInput id={id} size="s" className="input-inline mono" aria-label={rest['aria-label'] ?? 'Colour'} value={text} disabled={disabled} aria-invalid={!valid || undefined} style={{ width: 110 }} onChange={(e) => setText(e.target.value.toUpperCase())} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } if (e.key === 'Escape') setText(normalized); }} />
      <span className="swatch" style={{ background: normalized.slice(0, 7) }} aria-hidden="true" />
    </span>
  );
}

export function TagInput({ value, onChange, suggestions = [], disabled, id, label = 'Tags' }: { value: string[]; onChange: (tags: string[]) => void; suggestions?: string[]; disabled?: boolean; id?: string; label?: string }) {
  const [text, setText] = useState('');
  const add = (tag: string) => {
    const t = tag.trim();
    if (!t || value.includes(t)) {
      setText('');
      return;
    }
    onChange([...value, t]);
    setText('');
  };
  const listId = `${id ?? 'tags'}-suggestions`;
  return (
    <div className="tag-input" role="group" aria-label={label}>
      {value.map((t) => (
        <Chip key={t} onRemove={disabled ? undefined : () => onChange(value.filter((x) => x !== t))} removeLabel={`Remove tag ${t}`}>
          {t}
        </Chip>
      ))}
      <input
        id={id}
        list={listId}
        aria-label={`Add ${label.toLowerCase()}`}
        disabled={disabled}
        value={text}
        placeholder="Add tag…"
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            add(text);
          } else if (e.key === 'Backspace' && text === '' && value.length > 0) {
            onChange(value.slice(0, -1));
          }
        }}
        onBlur={() => text && add(text)}
      />
      <datalist id={listId}>
        {suggestions.filter((s) => !value.includes(s)).map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
    </div>
  );
}
